import { useState } from "react";
import { Documento, Usuario } from "../../types";
import { Modal } from "../common/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { SubirDocumentoModal } from "./SubirDocumentoModal";

interface DocumentosSectionProps {
  casoId: number;
  documentos: Documento[];
  usuarios: Usuario[];
  usuarioActual: Usuario;
  onDocumentoCreate: (documento: Omit<Documento, "id" | "fechaSubida">) => void;
  onDocumentoDelete: (id: number) => void;
}

export function DocumentosSection({
  casoId,
  documentos,
  usuarios,
  usuarioActual,
  onDocumentoCreate,
  onDocumentoDelete,
}: DocumentosSectionProps) {
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [documentoToDelete, setDocumentoToDelete] = useState<Documento | null>(null);

  const getUsuarioNombre = (id: number) =>
    usuarios.find((u) => u.id === id)?.nombre || "Usuario";

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getFileIcon = (tipo: string) => {
    switch (tipo) {
      case "pdf":
        return (
          <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v5a2 2 0 002 2h5v9H6z" />
          </svg>
        );
      case "imagen":
        return (
          <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M21 3H3a2 2 0 00-2 2v14a2 2 0 002 2h18a2 2 0 002-2V5a2 2 0 00-2-2zm0 16H3V5h18v14zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-4.5z" />
          </svg>
        );
      case "word":
        return (
          <svg className="w-8 h-8 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v5a2 2 0 002 2h5v9H6z" />
          </svg>
        );
      case "excel":
        return (
          <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v5a2 2 0 002 2h5v9H6z" />
          </svg>
        );
      default:
        return (
          <svg className="w-8 h-8 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v5a2 2 0 002 2h5v9H6z" />
          </svg>
        );
    }
  };

  const sortedDocumentos = [...documentos].sort(
    (a, b) => new Date(b.fechaSubida).getTime() - new Date(a.fechaSubida).getTime()
  );

  return (
    <div className="space-y-4">
      {/* Header con boton de subir */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-slate-700">
          Documentos ({documentos.length})
        </h3>
        <button
          onClick={() => setIsUploadOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Subir Documento
        </button>
      </div>

      {/* Grid de documentos */}
      {sortedDocumentos.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {sortedDocumentos.map((doc) => (
            <div
              key={doc.id}
              className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">{getFileIcon(doc.tipo)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">
                    {doc.nombre}
                  </p>
                  {doc.clasificacion && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {doc.clasificacion}
                    </span>
                  )}
                  <div className="mt-2 text-xs text-slate-500">
                    <p>{doc.tamano}</p>
                    {doc.costoAsociado !== undefined && doc.costoAsociado > 0 && (
                      <p className="text-emerald-600 font-medium">
                        Costo: ${doc.costoAsociado.toLocaleString("es-CL")}
                      </p>
                    )}
                    <p>
                      Subido por {getUsuarioNombre(doc.subidoPor)} el{" "}
                      {formatDateTime(doc.fechaSubida)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setDocumentoToDelete(doc)}
                  className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"
                  title="Eliminar"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-500">
          No hay documentos asociados a este caso
        </div>
      )}

      {/* Resumen de costos */}
      {(() => {
        const totalCosto = documentos.reduce((sum, d) => sum + (d.costoAsociado || 0), 0);
        return totalCosto > 0 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex justify-between items-center">
            <span className="text-sm text-emerald-700 font-medium">Costo total documentos</span>
            <span className="text-sm font-semibold text-emerald-800">
              ${totalCosto.toLocaleString("es-CL")}
            </span>
          </div>
        ) : null;
      })()}

      {/* Modal de subida */}
      <Modal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Subir Documento"
      >
        <SubirDocumentoModal
          casoId={casoId}
          usuarioId={usuarioActual.id}
          onSubmit={(data) => {
            onDocumentoCreate(data);
            setIsUploadOpen(false);
          }}
          onCancel={() => setIsUploadOpen(false)}
        />
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog
        isOpen={documentoToDelete !== null}
        onClose={() => setDocumentoToDelete(null)}
        onConfirm={() => {
          if (documentoToDelete) {
            onDocumentoDelete(documentoToDelete.id);
          }
        }}
        title="Eliminar Documento"
        message={`Esta seguro que desea eliminar el documento "${documentoToDelete?.nombre}"?`}
        confirmText="Eliminar"
      />
    </div>
  );
}
